import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Store type with states for multiple budgets
interface UiState {
  budgets: Record<
    string,
    {
      budgetSummaryCollapsed: boolean;
      budgetAssignedBalancedCollapsed: boolean;
      budgetAssignedUnbalancedCollapsed: boolean;
      budgetOverdraftCollapsed: boolean;
      budgetAutoAssignCollapsed: boolean;
      budgetQuickActionsCollapsed: boolean;
      budgetNotesCollapsed: boolean;
      bucketUpcomingCollapsed: boolean;
      bucketUnderfundedCollapsed: boolean;
      creditCardOverdraftCollapsed: boolean;
      accountErrorCollapsed: boolean;
      accountNavGroupsCollapsed: Record<string, boolean>; // Map of group name to collapsed state
    }
  >;

  setBudgetSummaryCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetAssignedBalancedCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetAssignedUnbalancedCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetOverdraftCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetAutoAssignCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetQuickActionsCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBudgetNotesCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBucketUpcomingCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setBucketUnderfundedCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setCreditCardOverdraftCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setAccountErrorCollapsed: (budgetId: string, isCollapsed: boolean) => void;
  setAccountNavGroupCollapsed: (budgetId: string, groupName: string, isCollapsed: boolean) => void;
}

// Default state for a single budget
const defaultBudgetState = {
  budgetSummaryCollapsed: false,
  budgetAssignedBalancedCollapsed: true, // Default to collapsed when balanced
  budgetAssignedUnbalancedCollapsed: false,
  budgetOverdraftCollapsed: false,
  budgetAutoAssignCollapsed: false,
  budgetQuickActionsCollapsed: false,
  budgetNotesCollapsed: false,
  bucketUpcomingCollapsed: false,
  bucketUnderfundedCollapsed: false,
  creditCardOverdraftCollapsed: false,
  accountErrorCollapsed: false, // Default to expanded (not collapsed)
  accountNavGroupsCollapsed: {} as Record<string, boolean>,
};

// Create the store with persistence
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      budgets: {},

      setBudgetSummaryCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetSummaryCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetAssignedBalancedCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetAssignedBalancedCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetAssignedUnbalancedCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetAssignedUnbalancedCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetOverdraftCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetOverdraftCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetAutoAssignCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetAutoAssignCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetQuickActionsCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetQuickActionsCollapsed: isCollapsed,
            },
          },
        })),

      setBudgetNotesCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              budgetNotesCollapsed: isCollapsed,
            },
          },
        })),

      setBucketUpcomingCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              bucketUpcomingCollapsed: isCollapsed,
            },
          },
        })),

      setBucketUnderfundedCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              bucketUnderfundedCollapsed: isCollapsed,
            },
          },
        })),

      setCreditCardOverdraftCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              creditCardOverdraftCollapsed: isCollapsed,
            },
          },
        })),

      setAccountErrorCollapsed: (budgetId, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              accountErrorCollapsed: isCollapsed,
            },
          },
        })),

      setAccountNavGroupCollapsed: (budgetId, groupName, isCollapsed) =>
        set((state) => ({
          budgets: {
            ...state.budgets,
            [budgetId]: {
              ...(state.budgets[budgetId] || defaultBudgetState),
              accountNavGroupsCollapsed: {
                ...(state.budgets[budgetId]?.accountNavGroupsCollapsed || {}),
                [groupName]: isCollapsed,
              },
            },
          },
        })),
    }),
    {
      name: 'budget-preferences',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

// Hook to get and set the budget summary collapsed state
export const useBudgetSummaryCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.budgetSummaryCollapsed ?? defaultBudgetState.budgetSummaryCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetSummaryCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget assigned balanced collapsed state
export const useBudgetAssignedBalancedCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) =>
      state.budgets[budgetId]?.budgetAssignedBalancedCollapsed ?? defaultBudgetState.budgetAssignedBalancedCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetAssignedBalancedCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget assigned unbalanced collapsed state
export const useBudgetAssignedUnbalancedCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) =>
      state.budgets[budgetId]?.budgetAssignedUnbalancedCollapsed ??
      defaultBudgetState.budgetAssignedUnbalancedCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetAssignedUnbalancedCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget assigned collapsed state
export const useBudgetAssignedCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) =>
      state.budgets[budgetId]?.budgetAssignedBalancedCollapsed ?? defaultBudgetState.budgetAssignedBalancedCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetAssignedBalancedCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget overdraft collapsed state
export const useBudgetOverdraftCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.budgetOverdraftCollapsed ?? defaultBudgetState.budgetOverdraftCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetOverdraftCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget auto assign collapsed state
export const useBudgetAutoAssignCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.budgetAutoAssignCollapsed ?? defaultBudgetState.budgetAutoAssignCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetAutoAssignCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget quick actions collapsed state
export const useBudgetQuickActionsCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.budgetQuickActionsCollapsed ?? defaultBudgetState.budgetQuickActionsCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetQuickActionsCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget notes collapsed state
export const useBudgetNotesCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.budgetNotesCollapsed ?? defaultBudgetState.budgetNotesCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBudgetNotesCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the bucket upcoming collapsed state
export const useBucketUpcomingCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.bucketUpcomingCollapsed ?? defaultBudgetState.bucketUpcomingCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBucketUpcomingCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the budget underfunded collapsed state
export const useBucketUnderfundedCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.bucketUnderfundedCollapsed ?? defaultBudgetState.bucketUnderfundedCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setBucketUnderfundedCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the credit card overdraft collapsed state
export const useCreditCardOverdraftCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.creditCardOverdraftCollapsed ?? defaultBudgetState.creditCardOverdraftCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setCreditCardOverdraftCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set the account error collapsed state
export const useAccountErrorCollapsed = (budgetId: string) => {
  const isCollapsed = useUiStore(
    (state) => state.budgets[budgetId]?.accountErrorCollapsed ?? defaultBudgetState.accountErrorCollapsed,
  );

  const setCollapsed = useUiStore((state) => state.setAccountErrorCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, value)] as const;
};

// Hook to get and set account nav group collapsed state
export const useAccountNavGroupCollapsed = (budgetId: string, groupName: string) => {
  const isCollapsed = useUiStore((state) => state.budgets[budgetId]?.accountNavGroupsCollapsed?.[groupName] ?? false);

  const setCollapsed = useUiStore((state) => state.setAccountNavGroupCollapsed);
  return [isCollapsed, (value: boolean) => setCollapsed(budgetId, groupName, value)] as const;
};
